<template>
  <div
    ref="container"
    class="site-header-container"
  >
    <header class="site-header">
      <div class="site-header__container">
        <!-- Left -->
        <div class="site-header__left">
          <button
            v-if="mobile.handle"
            class="site-header__menu icon-button"
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.menu')"
            />

            <icon-menu />
          </button>

          <button
            class="site-header__search icon-button"
            type="button"
            @click="mobileSearchbar = true"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.search')"
            />

            <icon-search />
          </button>

          <div
            v-if="mobileSearchbar"
            class="site-header__search--mobile"
          >
            <search-bar
              input-id="vue-mobile-search"
              :klevu="klevu"
              namespace="quicksearchMobile"
            />
            <button
              class="search-bar__cancel text-other-button-s-underline icon-button"
              type="button"
              @click="closeMobileSearch"
              v-text="$string('general.site_header.cancel')"
            />
          </div>

          <div
            v-else
            class="site-header__search--desktop"
          >
            <search-bar
              input-id="vue-desktop-search"
              :klevu="klevu"
              namespace="quicksearchLeft"
            />
          </div>

          <header-navigation
            :navigation="navigation"
            :liquid="liquid"
            :blocks="blocks"
          />
        </div>

        <!-- Logo -->
        <component
          :is="titleElement"
          class="site-header__center"
        >
          <a
            class="site-header__logo"
            :href="$variable('routes.root')"
          >
            <span
              id="site-header-title"
              class="visually-hidden"
              v-text="$variable('store.shopName')"
            />

            <brand-logo />
            
          </a>
        </component>

        <!-- Right -->
        <div class="site-header__right">
          <search-bar
            input-id="vue-quicksearch"
            :klevu="klevu"
            namespace="quicksearchRight"
          />
          <div
            v-if="locationToggle"
            class="promo-banner__country-select"
          >
            <country-selector-btn
              mobile-flag-only="true"
            />
          </div>
          <a
            class="site-header__account icon-button"
            :href="$variable('routes.account.url')"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.account')"
            />

            <icon-account />
          </a>

          <a
            class="site-header__cart icon-button"
            :href="$variable('routes.cart.url')"
            @click="handleCartToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.cart')"
            />

            <icon-cart />

            <span
              v-if="cart.item_count > 0"
              class="site-header__cart-badge critical-clear text-body-2xs-regular"
              aria-hidden="true"
              v-text="cart.item_count"
            />
          </a>
        </div>
      </div>
    </header>

    <!-- Menu drawer -->
    <teleport
      v-if="mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        type="drawer"
        :show-close="false"
      >
        <template #body="overlay">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :handle="navigation.handle"
            :item-count="navigation.itemCount"
            :liquid="liquid"
            :blocks="blocks"
            :overlay="overlay"
          />

          <div
            v-if="!getComponentActive('menu-drawer')"
            class="navigation__menu-drawer"
          >
            <div
              v-for="(_, index) of [...Array(mobile.itemCount)]"
              :key="`menu-drawer-loading-${index}`"
              class="navigation__menu-drawer-link"
            >
              <span class="navigation__menu-drawer-loading" />
            </div>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import { debounce } from '~/helpers/general'
import Overlay from '~global/overlay/overlay'
import HeaderNavigation from '~async/header-navigation/header-navigation'
import SearchBar from '~global/search-bar/search-bar'
import CountrySelectorBtn from '~global/country-selector-btn/country-selector-btn'

import BrandLogo from '~icons/general/brand-logo.svg'
import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconMenu from '~icons/general/menu.svg'
import IconSearch from '~icons/general/search.svg'

export default {
  name: 'SiteHeader',

  components: {
    BrandLogo,
    IconAccount,
    IconCart,
    IconMenu,
    IconSearch,
    HeaderNavigation,
    CountrySelectorBtn,
    SearchBar,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    Overlay,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    navigation: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
    klevu: {
      type: Object,
      default: () => ({}),
    },
    locationToggle: {
      type: Boolean,
      default: true,
    },
    blocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      mobileSearchbar: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    setTimeout(() => {
      this.setSiteHeaderHeight()
    }, 0)

    window.addEventListener('resize', debounce(this.setSiteHeaderHeight, 250))

    if (this.locationToggle) {
      this.setLocationListeners()
    }
    window.addEventListener("DOMContentLoaded", function() {
    setTimeout(function(){ Invoca.PNAPI.run(); }, 5000);
    })
    
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
      setHeight: 'site-header/setHeight',
    }),

    /**
     * Set location event listeners
     */
    setLocationListeners() {
      cnvs.EventBus.listen('browser-context', () => {
        this.toggleOverlay({
          component: 'country-selector',
          ignoreDismissed: true,
          namespace: 'countrySelector',
        })
      })

      cnvs.EventBus.listen('cnvs:locationToggleListenerReady', () => {
        cnvs.EventBus.emit('cnvs:locationToggleReady')
      })
    },

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    setSiteHeaderHeight() {
      const headerHeight = this.$refs.container.offsetHeight
      this.setHeight(headerHeight)
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (
        !this.isMobile ||
        !this.getComponentExists('menu-drawer')
      ) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Close mobile searchbar.
     */
    closeMobileSearch() {
      this.mobileSearchbar = false
      this.closeOverlay('quicksearch')
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },
  },
}
</script>
